import React, { useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { Phone, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "../context/ThemeContext";
import { useLocation, useNavigate } from "react-router-dom";

const ACCENT = "#5cb85c";

export const Navbar = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const pathname = location?.pathname || "/";
  const isNavyPage = ["/liste-de-prix", "/a-savoir", "/avis-aux-utilisateurs"].some((p) =>
    pathname.startsWith(p)
  );

  const navVariant = isDark || isNavyPage ? "dark" : "light";
  const navLogo = navVariant === "dark" ? "/logo-light.svg" : "/logo.svg";

  const linkClass =
    navVariant === "light"
      ? "text-slate-700 hover:text-slate-900"
      : "text-white/80 hover:text-white";

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id, event) => {
    if (event) event.preventDefault();
    if (location.pathname === "/") {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        window.history.replaceState(null, "", `/#${id}`);
        setIsMobileMenuOpen(false);
      }
      return;
    }
    navigate(`/#${id}`);
  };

  const navLinks = [
    { label: t("nav.service"), id: "service", href: "/#service" },
    { label: t("nav.process"), id: "benefits", href: "/#benefits" },
    { label: t("nav.faq"), id: "faq", href: "/#faq" },
    { label: t("nav.contact"), id: "contact", href: "/#contact" },
  ];

  // ✅ IMPORTANT: sur les pages navy, même sans scroll, on met un fond léger sinon "on voit rien"
  const navChrome =
    navVariant === "light"
      ? isScrolled
        ? "bg-white/80 backdrop-blur-md border-b border-slate-200 shadow-sm"
        : "bg-transparent"
      : isScrolled
      ? "bg-[#070b12]/80 backdrop-blur-md border-b border-white/10 shadow-md"
      : isNavyPage
      ? "bg-[#070b12]/65 backdrop-blur-md border-b border-white/10"
      : "bg-transparent";

  return (
    <>
      <nav
        data-testid="navbar"
        className={[
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          navChrome,
          "py-3",
        ].join(" ")}
      >
        <div className="container-width">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="flex items-center"
              data-testid="logo-link"
            >
              <div
                className={[
                  "flex items-center justify-center rounded-2xl px-3.5 py-1.5 sm:px-4 sm:py-2",
                  "transition-all duration-300 ring-1",
                  navVariant === "light"
                    ? "bg-white/90 shadow-sm ring-slate-200/60"
                    : "bg-white/10 ring-white/10",
                ].join(" ")}
              >
                <img src={navLogo} alt="Éco-Bain" className="h-9 sm:h-10 md:h-11 w-auto object-contain" />
              </div>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  onClick={(e) => scrollToSection(link.id, e)}
                  className={"nav-link hover:underline font-medium transition-colors duration-200 " + linkClass}
                  data-testid={`nav-${link.id}`}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              <a
                href="tel:514-326-2246"
                className={"hidden md:flex items-center gap-2 font-medium transition-colors duration-200 " + linkClass}
                data-testid="phone-link"
              >
                <Phone size={18} />
                <span>{t("nav.phone")}</span>
              </a>

              {/* Theme toggle hidden on /liste-de-prix */}
              {location.pathname !== "/liste-de-prix" && (
                <ThemeToggle
                  className={
                    "bg-transparent " +
                    (navVariant === "light"
                      ? "text-slate-700 hover:text-slate-900 hover:bg-slate-200/60"
                      : "text-white/80 hover:text-white hover:bg-white/5")
                  }
                />
              )}

              <button
                onClick={toggleLanguage}
                type="button"
                aria-label={language === "fr" ? "Afficher le site en anglais" : "View the site in French"}
                className={
                  "flex items-center justify-center w-10 h-10 rounded-full bg-transparent font-semibold text-sm transition-colors " +
                  (navVariant === "light"
                    ? "text-slate-700 hover:text-slate-900 hover:bg-slate-200/60"
                    : "text-white/80 hover:text-white hover:bg-white/5")
                }
                data-testid="language-toggle"
              >
                {language === "fr" ? "EN" : "FR"}
              </button>

              <a href="/#contact" onClick={(e) => scrollToSection("contact", e)} className="hidden md:block btn-primary" data-testid="nav-cta">
                {t("nav.getQuote")}
              </a>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                type="button"
                aria-label={isMobileMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-navigation"
                className={
                  "lg:hidden flex items-center justify-center w-10 h-10 rounded-full bg-transparent transition-colors " +
                  (navVariant === "light"
                    ? "text-slate-700 hover:text-slate-900 hover:bg-slate-200/60"
                    : "text-white/80 hover:text-white hover:bg-white/5")
                }
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer */}
      <div aria-hidden className="h-20" />

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="mobile-navigation"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-white dark:bg-[#070b12] pt-24 px-6"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-6">
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  onClick={(e) => scrollToSection(link.id, e)}
                  className="text-left text-xl font-medium text-slate-800 dark:text-slate-100 hover:text-[var(--eco-accent)] py-2 border-b border-slate-100 dark:border-white/10"
                  style={{ "--eco-accent": ACCENT }}
                  data-testid={`mobile-nav-${link.id}`}
                >
                  {link.label}
                </a>
              ))}

              <a
                href="tel:514-326-2246"
                className="flex items-center gap-3 text-xl font-medium text-slate-800 dark:text-slate-100 hover:text-[var(--eco-accent)] py-2"
                style={{ "--eco-accent": ACCENT }}
                data-testid="mobile-phone-link"
              >
                <Phone size={22} />
                {t("nav.phone")}
              </a>

              <a href="/#contact" onClick={(e) => scrollToSection("contact", e)} className="btn-primary mt-4 text-center" data-testid="mobile-cta">
                {t("nav.getQuote")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
